// API Configuration and Client
import { auth } from './firebase';
import { getApiUrl } from './env';

const API_BASE_URL = getApiUrl();

interface ApiResponse<T> {
    data?: T;
    error?: string;
    details?: Record<string, unknown>;
}

interface RequestOptions extends RequestInit {
    params?: Record<string, string>;
}

/**
 * Get current Firebase ID token for authenticated requests.
 * The token auto-refreshes if it's expired or about to expire.
 */
async function getFirebaseToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) {
        console.debug('[API] No authenticated user, skipping token');
        return null;
    }
    try {
        // getIdToken(false) returns cached token, refreshes if < 5 min to expiry
        const token = await user.getIdToken(/* forceRefresh */ false);
        return token;
    } catch (error) {
        console.error('[API] Failed to get Firebase token:', error);
        return null;
    }
}

class ApiClient {
    private baseUrl: string;
    /** Mutex: if a refresh is in-flight, all other 401 retries wait for it */
    private refreshPromise: Promise<boolean> | null = null;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    /**
     * Attempt to refresh the access token using the stored refresh token.
     * Returns true if refresh succeeded, false otherwise.
     */
    private async refreshAccessToken(): Promise<boolean> {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            console.debug('[API] No refresh token available');
            return false;
        }

        try {
            console.debug('[API] Refreshing access token...');
            const response = await fetch(`${this.baseUrl}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken }),
            });

            if (!response.ok) {
                console.warn('[API] Token refresh failed:', response.status);
                // Refresh token is invalid — clear everything
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                return false;
            }

            const data = await response.json();
            localStorage.setItem('accessToken', data.accessToken);
            if (data.refreshToken) {
                localStorage.setItem('refreshToken', data.refreshToken);
            }
            console.debug('[API] Token refreshed successfully');
            return true;
        } catch (error) {
            console.error('[API] Token refresh error:', error);
            return false;
        }
    }

    /**
     * Deduplicated refresh: if a refresh is already in progress,
     * wait for it instead of firing another one.
     */
    private async tryRefresh(): Promise<boolean> {
        if (this.refreshPromise) {
            return this.refreshPromise;
        }
        this.refreshPromise = this.refreshAccessToken().finally(() => {
            this.refreshPromise = null;
        });
        return this.refreshPromise;
    }

    private async request<T>(
        endpoint: string,
        options: RequestOptions = {},
        isRetry = false
    ): Promise<ApiResponse<T>> {
        const { params, ...fetchOptions } = options;

        // Build URL with query params
        let url = `${this.baseUrl}${endpoint}`;
        if (params) {
            const searchParams = new URLSearchParams(params);
            url += `?${searchParams.toString()}`;
        }

        // Get auth token: prefer backend JWT from localStorage (set at login),
        // then fallback to Firebase ID token
        let token: string | null = localStorage.getItem('accessToken');
        if (!token) {
            token = await getFirebaseToken();
        }

        // Add headers including auth token if available
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        if (token) {
            (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
        }

        try {
            console.debug(`[API] ${options.method || 'GET'} ${endpoint}`, { hasToken: !!token });

            const response = await fetch(url, {
                ...fetchOptions,
                headers,
            });

            if (response.status === 401 && !isRetry) {
                const hadLocalToken = !!localStorage.getItem('accessToken');

                // Try backend JWT refresh first
                if (localStorage.getItem('refreshToken')) {
                    console.debug('[API] Got 401, attempting silent token refresh...');
                    const refreshed = await this.tryRefresh();
                    if (refreshed) {
                        return this.request<T>(endpoint, options, true);
                    }
                }

                // Refresh failed (or no refresh token) — if we sent a stale localStorage
                // token, clear it and retry using the Firebase ID token instead.
                // Firebase tokens are auto-refreshed by the SDK and never go stale.
                if (hadLocalToken) {
                    console.debug('[API] Clearing stale localStorage token, retrying with Firebase token');
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    return this.request<T>(endpoint, options, true);
                }
            }

            // Handle empty responses (204 No Content)
            const contentType = response.headers.get('content-type');
            let data: unknown = null;
            if (contentType?.includes('application/json')) {
                data = await response.json();
            }

            if (!response.ok) {
                const errorData = data as { error?: string; details?: Record<string, unknown> } | null;
                console.error(`[API] Request failed: ${response.status}`, errorData);
                return {
                    error: errorData?.error || `Request failed with status ${response.status}`,
                    details: errorData?.details,
                };
            }

            return { data: data as T };
        } catch (error) {
            console.error('[API] Network error:', error);
            return {
                error: error instanceof Error ? error.message : 'Network error - is the backend running?',
            };
        }
    }

    // GET request
    async get<T>(endpoint: string, params?: Record<string, string>): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { method: 'GET', params });
    }

    // POST request
    async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: 'POST',
            body: body ? JSON.stringify(body) : undefined,
        });
    }

    // PATCH request
    async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: 'PATCH',
            body: body ? JSON.stringify(body) : undefined,
        });
    }

    // DELETE request
    async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { method: 'DELETE' });
    }
}

// Create singleton instance
export const api = new ApiClient(API_BASE_URL);

// Export for type checking
export type { ApiResponse };

